// authFetch.js
import API_BASE_URL from './config';

const refreshToken = async () => {
  const refresh = localStorage.getItem('refreshToken');
  if (!refresh) {
    throw new Error('No refresh token available.');
  }

  const response = await fetch(`${API_BASE_URL}/api/token/refresh/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refresh }),
  });

  if (!response.ok) {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    window.location.href = '/signin';
    throw new Error('Session expired. Please log in again.');
  }

  const data = await response.json();
  localStorage.setItem('accessToken', data.access);
  return data.access;
};

const buildHeaders = (options, token) => {
  const headers = new Headers(options.headers || {});

  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  // Let the browser set Content-Type (including the multipart boundary) for
  // FormData. For everything else, default to JSON unless already specified.
  if (options.body instanceof FormData) {
    headers.delete('Content-Type');
  } else if (!headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  return headers;
};

const parseErrorResponse = async (response) => {
  let errorData = {};
  try {
    errorData = await response.json();
  } catch {
    // Response body wasn't JSON (e.g. 502 nginx HTML page) — leave errorData empty.
  }

  // Normalise: our DRF views return { detail } for generic errors and
  // { field: [...messages] } for validation errors. Build a single human-
  // readable message and also expose the raw data for field-level display.
  const message =
    errorData.detail ||
    errorData.error ||        // e.g. { error: 'No file was uploaded.' }
    errorData.non_field_errors?.[0] ||
    `Request failed with status ${response.status}.`;

  const error = new Error(message);
  error.data = errorData;     // Let callers access field-level errors via error.data
  error.status = response.status;
  return error;
};

export const authFetch = async (url, options = {}) => {
  let token = localStorage.getItem('accessToken');
  let headers = buildHeaders(options, token);

  let response = await fetch(`${API_BASE_URL}${url}`, { ...options, headers });

  // --- Token refresh ---
  // IMPORTANT: FormData bodies can only be read once by the browser. We cannot
  // simply retry with the same `options.body` if it is a FormData instance —
  // the stream will already be consumed. Instead we surface a clear auth error
  // so the user can re-authenticate rather than silently sending an empty body.
  if (response.status === 401) {
    if (options.body instanceof FormData) {
      // Refresh the token for future requests but do NOT retry this upload —
      // it would send an empty body. Tell the user to try again.
      try {
        await refreshToken();
      } catch {
        // refreshToken() already redirects to /signin on failure.
      }
      const error = new Error('Your session was refreshed. Please try uploading again.');
      error.status = 401;
      throw error;
    }

    // For non-FormData requests, retry transparently as before.
    try {
      const newToken = await refreshToken();
      headers = buildHeaders(options, newToken);
      response = await fetch(`${API_BASE_URL}${url}`, { ...options, headers });
    } catch (error) {
      throw error;
    }
  }

  if (!response.ok) {
    throw await parseErrorResponse(response);
  }

  return response;
};